import {
  useModalContext,
  ADDRESS_MODAL,
  TERMS_OF_USE_MODAL,
  LOGIN_REGISTRATION_MODAL,
} from 'contexts/modal';
import { useUserContext, getUserFromCookies } from 'contexts/user';
import { GTM_CLICK_HOMEPAGE_SOLD_SIGN_IN } from 'constants/events';
import { AnalyticsController } from 'analytics/analytics-controller';
import { useLocale } from 'hooks/use-locale';
import { useTenantId } from 'hooks/use-tenant-id';
import { getLocalizedUrl } from 'utils/locale/get-localized-url';
import { TrackMapListingCardClicked } from 'components/search-es/tracking';
import { getComplianceRequirementFromCookies } from 'utils/compliance';
import { ComplianceRequirement } from 'types/compliance';

interface OnListingCardClickOptions {
  isHomePage?: boolean;
  isMapSidePanel?: boolean;
}

export type OnListingCardClickProps = {
  event?: unknown;
  isNoModalLocation?: boolean;
  addressPathWithFallback: string;
  listingId?: number;
  showsListing?: boolean;
  isPrivate: boolean;
  isSignInAndTermsOfUseRequired: boolean;
  isTermsRequired?: boolean;
  providerId?: number;
};

export function useOnListingCardClick({ isHomePage = false, isMapSidePanel = false }: OnListingCardClickOptions = {}) {
  const { openModal } = useModalContext();
  const { isAuthenticated } = useUserContext();
  const tenantId = useTenantId();
  const { currentLocale } = useLocale(tenantId);
  const openAddressPathWithFallback = (addressPathWithFallback: string, listingId?: number) => {
    const localizedUrl = getLocalizedUrl({
      locale: currentLocale,
      tenantId,
      path: addressPathWithFallback,
    });
    const separator = localizedUrl.includes('?') ? '&' : '?';
    const listingIdParam = listingId && listingId > 0 ? `${separator}listingId=${listingId}` : '';
    window.open(`${localizedUrl}${listingIdParam}`, '_blank');
  };

  const openProviderModal = (providerId: number) => {
    openModal(TERMS_OF_USE_MODAL, { providerId });
  };

  const openAuthModal = (callback?: () => void) => {
    openModal(LOGIN_REGISTRATION_MODAL, callback ? { callback } : {});
  };

  const openListingModal = (addressPathWithFallback: string, listingId?: number) => {
    openModal(ADDRESS_MODAL, { addressPath: addressPathWithFallback, listingId });
  };

  if (isHomePage) {
    const homePageClickFunction = ({ addressPathWithFallback, listingId, isPrivate, isSignInAndTermsOfUseRequired, isTermsRequired, providerId }: OnListingCardClickProps) => {
      if ((isSignInAndTermsOfUseRequired || isPrivate) && !isAuthenticated) {
        // Create callback that checks if terms are actually required after login
        // This ensures we don't open terms modal if user already accepted terms previously
        const callback = (isTermsRequired && providerId) ? () => {
          // Use Promise.resolve().then() to ensure user cookie is available after login
          // The user cookie is set immediately after login, but we wait a tick for React state updates
          Promise.resolve().then(() => {
            // Get user ID directly from cookies (available immediately after login)
            // This is more reliable than waiting for React context to update
            const userFromCookies = getUserFromCookies();
            const currentUserId = userFromCookies?.id;
            const complianceRequirement = getComplianceRequirementFromCookies(providerId, currentUserId);
            // Only open terms modal if terms are actually still required
            if (complianceRequirement === ComplianceRequirement.TERMS_OF_USE) {
              openProviderModal(providerId);
            }
          });
        } : undefined;
        openAuthModal(callback);
        AnalyticsController.trackEvent('Sold_Prices_Overlay');
        AnalyticsController.trackEvent(GTM_CLICK_HOMEPAGE_SOLD_SIGN_IN);
      }
      else if (isTermsRequired && providerId) {
        openProviderModal(providerId);
      }
      else {
        openAddressPathWithFallback(addressPathWithFallback, listingId);
      }
    };
    return homePageClickFunction;
  }

  else {
    const listingCardClickFunction = ({ event, isNoModalLocation, addressPathWithFallback, listingId, showsListing, isPrivate, isSignInAndTermsOfUseRequired }: OnListingCardClickProps) => {
      if (isMapSidePanel) {
        TrackMapListingCardClicked();
      }
      if (isPrivate || ((isSignInAndTermsOfUseRequired || !showsListing) && !isAuthenticated)) {
        openListingModal(addressPathWithFallback, listingId);
      }
      else if (isNoModalLocation) {
        openAddressPathWithFallback(addressPathWithFallback, listingId);
      }
      else if (((event as MouseEvent).target as Element).tagName !== 'A') {
        openListingModal(addressPathWithFallback, listingId);
      }
    };
    return listingCardClickFunction;
  }
}